/**
 * Customer View Schemas
 *
 * Zod schemas for the Customer Views API — saved views with filters, sorts,
 * groups, column config, and display options for the contacts table.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../../common/response.schema";

// =============================================================================
// Filter System — AND/OR nesting (max 3 levels)
// =============================================================================

/**
 * A single filter condition (leaf node in the filter tree)
 */
export const FilterConditionSchema = z.object({
  id: z.string().min(1),
  field: z.string().min(1),
  operator: z.string().min(1),
  value: z.unknown().optional()
});

export type FilterCondition = z.infer<typeof FilterConditionSchema>;

/**
 * Recursive filter group — AND/OR conjunction with nested conditions and groups.
 * Max depth enforced at runtime (3 levels).
 */
export const FilterGroupSchema: z.ZodType<FilterGroup> = z.lazy(() =>
  z.object({
    conjunction: z.enum(["and", "or"]),
    conditions: z.array(z.union([FilterConditionSchema, FilterGroupSchema]))
  })
);

export type FilterGroup = {
  conjunction: "and" | "or";
  conditions: (FilterCondition | FilterGroup)[];
};

/**
 * Validate filter group depth does not exceed max levels
 */
export function validateFilterDepth(group: FilterGroup, maxDepth = 3, currentDepth = 0): boolean {
  if (currentDepth >= maxDepth) return false;
  for (const condition of group.conditions) {
    if ("conjunction" in condition) {
      if (!validateFilterDepth(condition as FilterGroup, maxDepth, currentDepth + 1)) {
        return false;
      }
    }
  }
  return true;
}

// =============================================================================
// Sort Configuration
// =============================================================================

export const SortRuleSchema = z.object({
  field: z.string().min(1),
  direction: z.enum(["asc", "desc"])
});

export type SortRule = z.infer<typeof SortRuleSchema>;

// =============================================================================
// Column Configuration
// =============================================================================

export const AggregationTypeSchema = z.enum([
  "none",
  "count",
  "count_distinct",
  "sum",
  "avg",
  "min",
  "max",
  "percent_filled",
  "percent_empty"
]);

export type AggregationType = z.infer<typeof AggregationTypeSchema>;

export const ColumnConfigSchema = z.object({
  fieldKey: z.string().min(1),
  visible: z.boolean().default(true),
  width: z.number().int().min(50).max(800).optional(),
  aggregation: AggregationTypeSchema.default("none")
});

export type ColumnConfig = z.infer<typeof ColumnConfigSchema>;

// =============================================================================
// Grouping
// =============================================================================

export const GroupConfigSchema = z.object({
  field: z.string().min(1),
  direction: z.enum(["asc", "desc"]).default("asc"),
  showEmptyGroups: z.boolean().default(false)
});

export type GroupConfig = z.infer<typeof GroupConfigSchema>;

// =============================================================================
// Conditional Coloring
// =============================================================================

export const ColorRuleSchema = z.object({
  id: z.string().min(1),
  field: z.string().min(1),
  operator: z.string().min(1),
  value: z.unknown().optional(),
  color: z.string().min(1),
  target: z.enum(["row", "cell"]).default("row")
});

export type ColorRule = z.infer<typeof ColorRuleSchema>;

// =============================================================================
// Layout Types
// =============================================================================

export const ViewLayoutSchema = z.enum(["table", "kanban", "calendar"]);
export type ViewLayout = z.infer<typeof ViewLayoutSchema>;

export const RowDensitySchema = z.enum(["compact", "normal", "comfortable", "tall"]);
export type RowDensity = z.infer<typeof RowDensitySchema>;

// =============================================================================
// Full View Configuration
// =============================================================================

/**
 * Default columns for the contacts table — used when creating views
 * without explicit column config
 */
export const DEFAULT_VIEW_COLUMNS: ColumnConfig[] = [
  { fieldKey: "displayName", visible: true, width: 240, aggregation: "none" },
  { fieldKey: "primaryEmail", visible: true, width: 200, aggregation: "none" },
  { fieldKey: "primaryPhone", visible: true, width: 150, aggregation: "none" },
  { fieldKey: "tags", visible: true, width: 160, aggregation: "none" },
  { fieldKey: "totalSessions", visible: true, width: 100, aggregation: "none" },
  { fieldKey: "totalMessages", visible: true, width: 100, aggregation: "none" },
  { fieldKey: "lastSeenAt", visible: true, width: 150, aggregation: "none" }
];

export const ViewConfigSchema = z.object({
  // Filtering — root group defaults to empty AND
  filters: FilterGroupSchema.default({
    conjunction: "and",
    conditions: []
  }),

  // Sorting — multi-field, ordered (max 5)
  sorts: z
    .array(SortRuleSchema)
    .max(5)
    .default([{ field: "lastSeenAt", direction: "desc" }]),

  // Grouping — up to 2 levels
  groups: z.array(GroupConfigSchema).max(2).default([]),

  // Column configuration — ordered, per-view visibility/width
  columns: z.array(ColumnConfigSchema).min(1).max(50).default(DEFAULT_VIEW_COLUMNS),

  // Layout mode
  layout: ViewLayoutSchema.default("table"),

  // Display options
  rowDensity: RowDensitySchema.default("normal"),

  // Kanban-specific
  kanbanField: z.string().optional(),
  kanbanColumnOrder: z.array(z.string()).optional(),

  // Calendar-specific
  calendarDateField: z.string().optional(),
  calendarView: z.enum(["month", "week"]).optional(),

  // Frozen columns count (from left)
  frozenColumnCount: z.number().int().min(0).max(5).default(1),

  // Conditional coloring
  colorRules: z.array(ColorRuleSchema).max(20).default([]),

  // Search persisted per view
  search: z.string().optional()
});

export type ViewConfig = z.infer<typeof ViewConfigSchema>;

// =============================================================================
// View Visibility
// =============================================================================

export const ViewVisibilitySchema = z.enum(["PERSONAL", "SHARED", "SHARED_LOCKED"]);
export type ViewVisibility = z.infer<typeof ViewVisibilitySchema>;

// =============================================================================
// View CRUD Schemas
// =============================================================================

export const CreateViewBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  icon: z.string().max(10).optional(),
  description: z.string().max(500).optional(),
  visibility: ViewVisibilitySchema.default("PERSONAL"),
  config: ViewConfigSchema
});

export type CreateViewBody = z.infer<typeof CreateViewBodySchema>;

export const UpdateViewBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  icon: z.string().max(10).optional(),
  description: z.string().max(500).optional(),
  visibility: ViewVisibilitySchema.optional(),
  isDefault: z.boolean().optional(),
  isPinned: z.boolean().optional(),
  config: ViewConfigSchema.partial().optional()
});

export type UpdateViewBody = z.infer<typeof UpdateViewBodySchema>;

export const DuplicateViewBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional()
});

export type DuplicateViewBody = z.infer<typeof DuplicateViewBodySchema>;

export const ReorderViewsBodySchema = z.object({
  viewIds: z.array(z.string().uuid()).min(1)
});

export type ReorderViewsBody = z.infer<typeof ReorderViewsBodySchema>;

export const ViewPathVarsSchema = z.object({
  viewId: z.string().uuid()
});

export const ListViewsQuerySchema = z.object({
  includeShared: z.coerce.boolean().default(true)
});

export type ListViewsQuery = z.infer<typeof ListViewsQuerySchema>;

// =============================================================================
// View Response Schema
// =============================================================================

export const ViewResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  createdBy: z.string(),
  name: z.string(),
  slug: z.string(),
  icon: z.string().nullable(),
  description: z.string().nullable(),
  visibility: ViewVisibilitySchema,
  isDefault: z.boolean(),
  isPinned: z.boolean(),
  config: ViewConfigSchema,
  position: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export type ViewResponse = z.infer<typeof ViewResponseSchema>;

// =============================================================================
// Wrapped Response Schemas
// =============================================================================

export const ViewListResponseSchema = ApiSuccessSchema(z.array(ViewResponseSchema));
export const ViewDetailResponseSchema = ApiSuccessSchema(ViewResponseSchema);
export const ViewDeleteResponseSchema = ApiSuccessSchema(z.object({ deleted: z.boolean() }));
